import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Store, Phone, Package, MapPin, Boxes } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

// Phase 13: UNIT-ONLY direct-sales history. No money. No payment mode.
// Phase B: Manager can also view their own direct-sale history.
export default function SalesHistory() {
  const nav = useNavigate();
  const { t, displayName } = useLang();
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const homePath = isManager ? "/manager" : "/salesman";
  const newSalePath = isManager ? "/manager/direct-sale" : "/salesman/direct-sale";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Filter to "my own direct sales": for salesman the role-scoping in /orders
    // already restricts to assigned_salesman_id == self; for manager, manager would
    // otherwise see the whole district, so explicitly scope by their own user id.
    const params = isManager
      ? { source: "manager_direct", salesman_id: user.id }
      : { source: "salesman_direct" };
    api.get("/orders", { params })
      .then(r => setOrders(r.data || []))
      .finally(() => setLoading(false));
  }, [isManager, user]);

  const totalUnits = orders.reduce((s, o) => s + (o.total_units || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={t("my_direct_sales")} onBack={() => nav(homePath)} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        <div className="grid grid-cols-2 gap-3">
          <div className="stat-card" data-testid="ds-stat-count">
            <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("orders")}</div>
            <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">{orders.length}</div>
          </div>
          <div className="stat-card" data-testid="ds-stat-units">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-[#5856D6] grid place-items-center">
                <Boxes className="w-4 h-4 text-white" />
              </div>
              <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("units_sold")}</div>
            </div>
            <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">{totalUnits}</div>
          </div>
        </div>

        <button
          onClick={() => nav(newSalePath)}
          data-testid="ds-history-new-btn"
          className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-2xl py-4 text-lg font-bold flex items-center justify-center gap-2 shadow-md"
        >
          <Plus className="w-5 h-5" /> {t("new_direct_sale")}
        </button>

        {loading ? (
          <div className="text-center text-[#86868B] py-8">{t("loading")}</div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center soft-shadow" data-testid="ds-empty">
            <Package className="w-12 h-12 mx-auto text-[#86868B] mb-2" />
            <div className="text-[#86868B]">{t("no_direct_sales")}</div>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(o => (
              <div key={o.id} className="bg-white rounded-2xl p-4 soft-shadow" data-testid={`ds-history-${o.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="heading-font font-bold text-base text-[#1D1D1F] flex items-center gap-1.5">
                      <Store className="w-4 h-4 text-[#0055FF]" />
                      <span className="truncate">{o.shop_name}</span>
                      {o.shop_matched ? (
                        <span className="pill bg-green-50 text-green-700 text-[10px]">{t("shop_matched")}</span>
                      ) : (
                        <span className="pill bg-orange-50 text-orange-700 text-[10px]">{t("walk_in")}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#86868B]">{o.order_date}</div>
                  </div>
                  <div className="text-right">
                    <div className="heading-font text-lg font-bold text-[#1D1D1F]">{o.total_units} {t("units")}</div>
                    <div className="text-xs text-[#86868B]">{o.items?.length || 0} {t("items")}</div>
                  </div>
                </div>
                {o.items && o.items.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {o.items.slice(0, 4).map((it, i) => (
                      <span key={i} className="pill bg-gray-100 text-gray-700 text-xs">{displayName(it)} ×{it.quantity}</span>
                    ))}
                    {o.items.length > 4 && <span className="pill bg-gray-100 text-gray-700 text-xs">+{o.items.length - 4}</span>}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-xs text-[#86868B] mt-2">
                  {o.shop_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {o.shop_phone}</span>}
                  {o.delivery_geo && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#34C759]" /> Geo</span>}
                  {o.shop_address && <span className="truncate max-w-[60%]">{o.shop_address}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
